import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { DOWNLOAD_ORIGIN_BY_SURFACE, DOWNLOAD_SURFACES } from '#~/analytics/types/properties.js';
import type {
  DownloadEntryPoint,
  DownloadOrigin,
  DownloadSource,
  DownloadSurface,
  EventProperties,
  InstallerFailureReason
} from '#~/analytics/types/properties.js';
import { isElectronApp } from '#~/electron/utils.js';
import type { AnalyticsTrackFn } from './types.js';

/**
 * Download-lifecycle domain tracker: the whole of onboarding stage 1. Owns the
 * download-start pair (ENG-4347, `download_electron_app` →
 * `download_app_started`) and the install-failure terminal (ENG-4310,
 * `installer_failed`), so call sites emit through ONE module and can never fire
 * the canonical start without its legacy dual-write twin (or vice versa) during
 * the parity window.
 *
 * Per rule: think-in-systems — the dual-write invariant and the shared
 * attempt-id contract live here, at the domain boundary, not copy-pasted into
 * every vendor's download component. Mirrors the established
 * `device-lifecycle.ts` dual-write shape (ENG-3479).
 */

interface DownloadAppStartedProperties extends EventProperties {
  // Required (not optional) here even though EventProperties declares them
  // optional: the canonical-event-map contract for `download_app_started`
  // lists both as required_properties, so tsc must reject a call site that
  // omits them rather than PostHog discovering the gap at parity-check time.
  download_source: DownloadSource;
  download_entry_point: DownloadEntryPoint;
  // Escape hatch mirroring device-lifecycle's dual_write_legacy_* flag.
  // Defaults ON; stage-2 (cutover) flips call sites to false before the
  // legacy constant is deleted.
  dual_write_legacy_download_electron_app?: boolean;
}

interface InstallerFailedProperties extends EventProperties {
  // These are required (not optional) here even though EventProperties declares
  // them optional: the canonical-event-map contract for `installer_failed` lists
  // them as required_properties, and the e2e receipt rejects any emission
  // missing one. tsc must catch that at the call site, not the CI run.
  installer_failure_reason: InstallerFailureReason;
  download_source: DownloadSource;
  download_entry_point: DownloadEntryPoint;
  // The platform the user ASKED for. On the artifact-unavailable path this is
  // the whole diagnosis, so it must survive even when the value is unrecognized
  // — callers pass the raw route value rather than dropping the property.
  operating_system: string;
  architecture: string;
}

/**
 * Synthesize a per-initiation attempt id. Guards `globalThis.crypto?.randomUUID`
 * and falls back to a time+random id when absent — the bare `crypto.randomUUID()`
 * global is NOT present in the jest (jsdom/node-VM) environment, so the guard is
 * required for the unit suite to run. Mirrors `calibration/commands/attempt-id.ts`.
 */
const createDownloadAttemptId = (): string => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

/**
 * Which surface this renderer is, for download-origin purposes.
 *
 * Reads the same `isElectronApp()` signal the envelope already uses for its
 * `client` property (analytics/utils/context-resolver.ts), so the reported
 * origin and the envelope surface can never disagree.
 */
export const resolveDownloadSurface = (): DownloadSurface =>
  isElectronApp() ? DOWNLOAD_SURFACES.ELECTRON_APP : DOWNLOAD_SURFACES.WEB;

/**
 * Which surface this download started on, as the (source, entry point) pair.
 *
 * The agent-setup download page is one component rendered on two surfaces: in
 * the browser at studio.tryiris.ai, and inside the installed desktop app. Only
 * the browser one can be a user's FIRST download — the app does not exist yet —
 * so that is journey stage 1, and it must be distinguishable from an in-app
 * re-download for the download-to-open join to have anything to select on.
 *
 * Keyed, through `resolveDownloadSurface`, on the same `isElectronApp()` signal
 * the envelope uses for its `client` property, so the reported origin and the
 * envelope surface can never disagree.
 *
 * `surface` is an explicit parameter defaulting to the resolver, and product
 * code never passes it. It exists so the WEB origin is reachable from a test:
 * `isElectronApp()` caches its answer at module load from `window.electron`,
 * and the Playwright suite only ever drives an Electron renderer where that
 * flag is permanently true — so no click path in CI can reach the web branch,
 * and deleting `window.electron` at runtime would break the app rather than
 * flip the cached flag. The e2e probe (test-probes/download-origin-probe.ts)
 * supplies the web value and everything downstream of it — the dual-write, the
 * envelope, the before_send chain, the ingest POST — stays the real path. The
 * surface RESOLUTION is covered by the jest cases in download-lifecycle.test.ts.
 *
 * Per rule: think-in-systems (ai.knowledge/global) — resolved once here at the
 * domain boundary, like the dual-write invariant above, rather than re-derived
 * in each vendor's download component where the two could drift apart.
 */
export const resolveDownloadOrigin = (
  surface: DownloadSurface = resolveDownloadSurface()
): DownloadOrigin => DOWNLOAD_ORIGIN_BY_SURFACE[surface];

export const createDownloadLifecycleTracker = (track: AnalyticsTrackFn) => ({
  /**
   * Emit the download-start pair. MUST be called only at actual download
   * initiation (immediately before the synthetic `link.click()`), never on
   * page mount or OS selection — the funnel this event powers counts real
   * download starts, and a mount-time emit would silently inflate it.
   */
  trackDownloadAppStarted: ({
    dual_write_legacy_download_electron_app = true,
    ...properties
  }: DownloadAppStartedProperties): void => {
    // One id minted per initiation and shared verbatim by both emits: it is
    // the 1:1 join key for the PostHog old-vs-new parity check that gates
    // retiring the legacy event.
    const canonicalProperties = {
      ...properties,
      download_attempt_id: createDownloadAttemptId()
    };

    track(EVENT_NAMES.DOWNLOAD_APP_STARTED, EVENT_CATEGORIES.USER, canonicalProperties);

    if (dual_write_legacy_download_electron_app) {
      // Migration-only dual-write (ENG-4347 stage 1). The legacy emit keeps
      // the exact pre-rename name and USER category so existing dashboards
      // are untouched; remove after the ≥30-day window once parity is proven.
      track(EVENT_NAMES.USER_DOWNLOAD_ELECTRON_APP, EVENT_CATEGORIES.USER, canonicalProperties);
    }
  },

  /**
   * Emit the stage-1 install-failure terminal (ENG-4310). Net-new: nothing fires
   * for this today, so there is deliberately no dual-write leg — a second emit
   * under a legacy name would invent a funnel row no dashboard expects.
   *
   * Call this ONLY when the install genuinely cannot proceed. A false positive
   * is worse than the current silence: any alert built on this event would fire
   * on downloads that worked.
   *
   * Same USER category as its `download_app_started` sibling so the started/
   * failed halves of stage 1 stay queryable as one family.
   */
  trackInstallerFailed: (properties: InstallerFailedProperties): void => {
    // Always a fresh id, never one threaded in from a download start. Every
    // reason in INSTALLER_FAILURE_REASONS fires BEFORE any download begins —
    // there is no artifact to start — so there is no started row to join to, and
    // accepting a caller id would advertise a started↔failed join that no call
    // site can actually produce. A distinct id per failure is what keeps a
    // user's repeated attempts countable as separate attempts.
    track(EVENT_NAMES.INSTALLER_FAILED, EVENT_CATEGORIES.USER, {
      ...properties,
      download_attempt_id: createDownloadAttemptId()
    });
  }
});
